import React, { useState, useContext, useEffect } from "react";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { AuthContext } from "../App";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { 
  Flower2, 
  ArrowLeft, 
  Save,
  Palette,
  MessageSquare,
  BookOpen,
  Sparkles,
  Check,
  ChevronLeft,
  ChevronRight,
  Package,
  ExternalLink,
  Download,
  Lock,
  FileText
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const VERIFY_API = 'https://start.bloomwrightstudio.com/api';  // Verify against checkout subdomain
const PAYPAL_CLIENT_ID = "Ae_O9morsIW0sXAH4lUgaH2epHecxsbaHN4li4kllPElnAwozy9p20-0A0ttpeIrBggowDjYnz9KRVnA";

// Helper to get color palette as array (handles both old and new format)
const getColorPaletteArray = (colorPalette) => {
  if (!colorPalette) return [];
  // New format: object with colors array
  if (colorPalette.colors && Array.isArray(colorPalette.colors)) {
    return colorPalette.colors;
  }
  // Old format: direct array
  if (Array.isArray(colorPalette)) {
    return colorPalette;
  }
  return [];
};

// Helper to get brand values as array of strings (handles both old and new format)
const getBrandValuesArray = (brandValues) => {
  if (!brandValues) return [];
  if (Array.isArray(brandValues)) {
    // Check if it's new format (array of objects with value/proof)
    if (brandValues.length > 0 && typeof brandValues[0] === 'object' && brandValues[0].value) {
      return brandValues.map(v => v.value);
    }
    // Old format: array of strings
    return brandValues;
  }
  return [];
};

const BrandResults = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { user } = useContext(AuthContext);
  const [selectedBrandIndex, setSelectedBrandIndex] = useState(0);
  const [isSaving, setIsSaving] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [showDownloadSuccess, setShowDownloadSuccess] = useState(false);
  const [hasPurchased, setHasPurchased] = useState(false);
  const [showCheckout, setShowCheckout] = useState(false);
  const [isCheckingPurchase, setIsCheckingPurchase] = useState(false);
  const [savedBrandId, setSavedBrandId] = useState(null);

  const brandData = location.state?.brandData;
  const questionnaire = location.state?.questionnaire;

  // Check purchase status when brand is saved (but not if already purchased via auto-activation)
  useEffect(() => {
    if (savedBrandId && !hasPurchased) {
      checkPurchaseStatus(savedBrandId);
    }
  }, [savedBrandId]);

  // Handle checkout success from URL params
  useEffect(() => {
    const checkoutStatus = searchParams.get('checkout');
    if (checkoutStatus === 'success' && savedBrandId) {
      checkPurchaseStatus(savedBrandId);
    }
  }, [searchParams, savedBrandId]);

  // Check purchase status - checks both local API and checkout subdomain
  const checkPurchaseStatus = async (brandId) => {
    setIsCheckingPurchase(true);
    try {
      // First check local API
      if (brandId) {
        const response = await axios.get(`${API}/brands/${brandId}/purchase-status`);
        if (response.data.purchased) {
          setHasPurchased(true);
          setIsCheckingPurchase(false);
          return;
        }
      }
      
      // Then check checkout subdomain by email
      const userEmail = user?.email;
      if (userEmail) {
        const verifyResponse = await fetch(`${VERIFY_API}/verify-payment?email=${encodeURIComponent(userEmail)}`);
        const verifyData = await verifyResponse.json();
        if (verifyData.has_purchase === true) {
          setHasPurchased(true);
        }
      }
    } catch (error) {
      console.error("Error checking purchase status:", error);
    } finally {
      setIsCheckingPurchase(false);
    }
  };

  // When opening checkout modal, check purchase status first
  const handleUnlockClick = async () => {
    await checkPurchaseStatus(savedBrandId);
    setShowCheckout(true);
  };

  if (!brandData || !brandData.brand_suggestions) {
    return (
      <div className="min-h-screen bg-[#FDFCF8] flex items-center justify-center">
        <div className="text-center">
          <p className="text-[#78716C] mb-4">No brand data found</p>
          <Button 
            onClick={() => navigate("/questionnaire")}
            className="bg-[#C77D63] hover:bg-[#B06A52] text-white rounded-full"
          >
            Start Over
          </Button>
        </div>
      </div>
    );
  }

  const suggestions = brandData.brand_suggestions;
  const selectedBrand = suggestions[selectedBrandIndex];
  const packagingVendors = brandData.packaging_vendors || [];

  const handleSaveBrand = async () => {
    setIsSaving(true);
    try {
      const brandToSave = {
        brand_name: selectedBrand.brand_name,
        tagline: selectedBrand.tagline,
        business_type: questionnaire.business_type,
        brand_values: selectedBrand.brand_values,
        target_audience: questionnaire.target_audience,
        tone_of_voice: selectedBrand.tone_of_voice,
        color_palette: selectedBrand.color_palette,
        brand_story: selectedBrand.brand_story,
        business_guide: selectedBrand.business_guide,
        packaging_vendors: packagingVendors
      };

      const response = await axios.post(`${API}/brands`, brandToSave);
      setSavedBrandId(response.data.brand_id);
      
      // Check if brand was auto-activated (user already paid on checkout subdomain)
      if (response.data.is_activated) {
        setHasPurchased(true);
        toast.success("Brand saved! Your Brand Guide is ready to download.");
      } else {
        toast.success("Brand saved! You can now unlock your Brand Guide.");
      }
    } catch (error) {
      console.error("Save error:", error);
      toast.error("Failed to save brand. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  // Download handler - triggers direct file download (product fulfillment)
  const handleDownload = async () => {
    if (!selectedBrand) {
      toast.error("No brand to download");
      return;
    }

    // Check if brand is saved and purchased
    if (!savedBrandId) {
      toast.error("Please save your brand first");
      return;
    }

    if (!hasPurchased) {
      setShowCheckout(true);
      return;
    }
    
    setIsDownloading(true);
    
    try {
      // Prepare brand data for PDF generation
      const brandDataForPdf = {
        ...selectedBrand,
        brand_id: savedBrandId,
        business_type: questionnaire?.business_type || '',
        target_audience: questionnaire?.target_audience || ''
      };
      
      console.log("Downloading brand guide:", brandDataForPdf.brand_name);
      
      // Use fetch for blob handling
      const response = await fetch(`${API}/brands/export-pdf`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ brand: brandDataForPdf, brand_id: savedBrandId })
      });
      
      if (!response.ok) {
        if (response.status === 401) {
          toast.error("Please sign in to download your Brand Guide.");
          return;
        } else if (response.status === 402) {
          toast.error("Please complete checkout to download your Brand Guide.");
          setShowCheckout(true);
          return;
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const blob = await response.blob();
      console.log("PDF blob received, size:", blob.size);
      
      // Verify we got a valid PDF
      if (blob.size < 1000) {
        throw new Error("PDF file appears to be empty or corrupted");
      }
      
      const filename = `${selectedBrand.brand_name.replace(/\s+/g, '_')}_Brand_Guide.pdf`;
      
      // Use the msSaveOrOpenBlob for Edge/IE, otherwise use download link
      if (window.navigator && window.navigator.msSaveOrOpenBlob) {
        window.navigator.msSaveOrOpenBlob(blob, filename);
      } else {
        // Create object URL and download link
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        link.style.display = 'none';
        document.body.appendChild(link);
        
        // Trigger click
        link.click();
        
        // Cleanup after delay
        setTimeout(() => {
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
        }, 1000);
      }
      
      // Show success state
      setShowDownloadSuccess(true);
      toast.success("Your Brand Guide is downloading! Check your downloads folder.");
      
    } catch (error) {
      console.error("Download error:", error);
      toast.error("Failed to download. Please try again.");
    } finally {
      setIsDownloading(false);
    }
  };

  // Create PayPal order
  const createOrder = async () => {
    try {
      const response = await axios.post(`${API}/brands/checkout`, {
        brand_id: savedBrandId
      });
      
      if (response.data.already_purchased) {
        setHasPurchased(true);
        setShowCheckout(false);
        toast.success("You already own this Brand Guide!");
        return null;
      }
      
      return response.data.order_id;
    } catch (error) {
      console.error("Checkout error:", error);
      toast.error("Failed to start checkout. Please try again.");
      throw error;
    }
  };

  // Capture PayPal payment
  const onApprove = async (data) => {
    try {
      const response = await axios.post(`${API}/brands/checkout/capture`, {
        order_id: data.orderID
      });
      
      if (response.data.success) {
        setHasPurchased(true);
        setShowCheckout(false);
        toast.success("Payment successful! You can now download your Brand Guide.");
      }
    } catch (error) {
      console.error("Capture error:", error);
      toast.error("Payment capture failed. Please contact support.");
    }
  };

  return (
    <PayPalScriptProvider options={{ "client-id": PAYPAL_CLIENT_ID, currency: "USD" }}>
    <div className="min-h-screen bg-[#FDFCF8]">
      {/* Navigation */}
      <nav className="nav-glass sticky top-0 z-50 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <Button 
            variant="ghost" 
            onClick={() => navigate("/dashboard")}
            className="flex items-center gap-2 text-[#2C332E] hover:bg-[#F5F0EB]"
            data-testid="back-to-dashboard-btn"
          >
            <ArrowLeft className="w-5 h-5" />
            Dashboard
          </Button>
          <div className="flex items-center">
            <span className="text-xl font-semibold text-[#2C332E]" style={{ fontFamily: "'Playfair Display', serif" }}>
              Bloomwright Studio
            </span>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex flex-col items-end">
              {/* Export button - changes based on state */}
              {!savedBrandId ? (
                <Button 
                  onClick={() => toast.info("Save your brand first to unlock your Brand Guide")}
                  variant="outline"
                  className="border-[#E6E1DB] text-[#78716C] rounded-full px-4"
                  data-testid="download-brand-btn-disabled"
                >
                  <Lock className="w-4 h-4 mr-2" />
                  Download Brand Guide
                </Button>
              ) : !hasPurchased ? (
                <Button 
                  onClick={handleUnlockClick}
                  variant="outline"
                  className="border-[#C77D63] text-[#C77D63] hover:bg-[#C77D63]/5 rounded-full px-4"
                  data-testid="unlock-brand-btn"
                >
                  <Lock className="w-4 h-4 mr-2" />
                  Unlock Brand Guide — $47
                </Button>
              ) : (
                <Button 
                  onClick={handleDownload}
                  disabled={isDownloading}
                  variant="outline"
                  className="border-[#C77D63] text-[#C77D63] hover:bg-[#C77D63]/5 rounded-full px-4"
                  data-testid="download-brand-btn"
                >
                  {isDownloading ? (
                    <>
                      <div className="spinner w-4 h-4 mr-2 border-2 border-[#C77D63] border-t-transparent rounded-full animate-spin"></div>
                      Preparing...
                    </>
                  ) : (
                    <>
                      <Download className="w-4 h-4 mr-2" />
                      Generate & Download PDF
                    </>
                  )}
                </Button>
              )}
            </div>
            <Button 
              onClick={handleSaveBrand}
              disabled={isSaving || savedBrandId}
              className={`rounded-full px-6 ${savedBrandId ? 'bg-[#8DA399] hover:bg-[#8DA399]' : 'bg-[#C77D63] hover:bg-[#B06A52]'} text-white`}
              data-testid="save-brand-btn"
            >
              {isSaving ? (
                <>
                  <div className="spinner w-4 h-4 mr-2 border-2 border-white border-t-transparent"></div>
                  Saving...
                </>
              ) : savedBrandId ? (
                <>
                  <Check className="w-4 h-4 mr-2" />
                  Brand Saved
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Save Brand
                </>
              )}
            </Button>
          </div>
        </div>
      </nav>

      {/* Checkout Modal */}
      {showCheckout && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-lg w-full p-6 shadow-xl max-h-[90vh] overflow-y-auto">
            
            {isCheckingPurchase ? (
              // Loading state
              <div className="text-center py-8">
                <div className="w-8 h-8 border-4 border-[#8DA399] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                <p className="text-[#78716C]">Checking your purchase status...</p>
              </div>
            ) : hasPurchased ? (
              // Already purchased - show download
              <div className="text-center">
                <div className="w-16 h-16 bg-[#8DA399]/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Check className="w-8 h-8 text-[#8DA399]" />
                </div>
                <h3 className="text-2xl text-[#2C332E] mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
                  Your Brand Guide is Ready
                </h3>
                <p className="text-[#78716C] mb-6">
                  You've already purchased access. Download your Brand Guide below.
                </p>
                <Button
                  onClick={handleDownload}
                  disabled={isDownloading}
                  className="w-full bg-[#8DA399] hover:bg-[#7A8F86] text-white rounded-full py-4 font-medium"
                >
                  {isDownloading ? "Preparing..." : "Download Brand Guide PDF"}
                </Button>
                <button 
                  onClick={() => setShowCheckout(false)}
                  className="mt-4 text-[#78716C] hover:text-[#2C332E]"
                >
                  Close
                </button>
              </div>
            ) : (
              // Not purchased - show PayPal checkout
              <>
                <div className="text-center mb-6">
                  <div className="w-16 h-16 bg-[#C77D63]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <FileText className="w-8 h-8 text-[#C77D63]" />
                  </div>
                  <h3 className="text-xl font-semibold text-[#2C332E] mb-2">
                    Your Brand Guide
                  </h3>
                  <p className="text-[#78716C] text-sm">
                    You've created a complete brand concept.<br/>
                    To download your Brand Guide PDF, continue to checkout.
                  </p>
                </div>

                {/* What's Included */}
                <div className="bg-[#FDFCF8] rounded-xl p-4 mb-6">
                  <h4 className="text-sm font-semibold text-[#2C332E] mb-3">Included with Your Purchase</h4>
                  <ul className="space-y-2 text-sm text-[#78716C]">
                    <li className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-[#8DA399] mt-0.5 flex-shrink-0" />
                      A downloadable Brand Guide PDF
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-[#8DA399] mt-0.5 flex-shrink-0" />
                      Your brand name, positioning, and tone
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-[#8DA399] mt-0.5 flex-shrink-0" />
                      Color palette and typography direction
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-[#8DA399] mt-0.5 flex-shrink-0" />
                      Logo style and packaging guidance
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-[#8DA399] mt-0.5 flex-shrink-0" />
                      Early next-step considerations
                    </li>
                  </ul>
                  <p className="text-xs text-[#A8A29E] mt-3 italic">
                    This overview is designed to help you reflect, refine, and decide what to build next.
                  </p>
                </div>

                {/* Product & Price */}
                <div className="border border-[#E6E1DB] rounded-xl p-4 mb-6">
                  <div className="flex justify-between items-center mb-2">
                    <span className="font-medium text-[#2C332E]">Create Your Brand — Brand Guide</span>
                    <span className="font-semibold text-[#2C332E]">$47</span>
                  </div>
                  <p className="text-xs text-[#78716C]">
                    A calm, structured snapshot of the brand you just created — delivered as a downloadable PDF.
                  </p>
                </div>

                {/* PayPal Buttons */}
                <div className="mb-4">
                  <PayPalButtons
                    style={{ layout: "vertical", shape: "pill" }}
                    createOrder={createOrder}
                    onApprove={onApprove}
                    onError={(err) => {
                      console.error("PayPal error:", err);
                      toast.error("Payment failed. Please try again.");
                    }}
                  />
                </div>

                {/* Reassurance */}
                <p className="text-xs text-[#78716C] text-center mb-4">
                  Your brand details are saved. You'll be able to download immediately after checkout.
                </p>

                {/* Close */}
                <button
                  onClick={() => setShowCheckout(false)}
                  className="w-full text-sm text-[#78716C] hover:text-[#2C332E] py-2"
                >
                  Cancel
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {/* Download Success Modal */}
      {showDownloadSuccess && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-xl">
            <div className="text-center">
              <div className="w-16 h-16 bg-[#8DA399]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Check className="w-8 h-8 text-[#8DA399]" />
              </div>
              <h3 className="text-xl font-semibold text-[#2C332E] mb-2">
                Your Brand Guide is downloading.
              </h3>
              <p className="text-[#78716C] text-sm mb-4">
                Your PDF will download once it&apos;s ready.
              </p>
              <p className="text-xs text-[#A8A29E] mb-6">
                Check your downloads folder. If the download didn't start, click below to try again.
              </p>
              
              {/* Re-download button */}
              <Button 
                onClick={handleDownload}
                disabled={isDownloading}
                variant="outline"
                className="border-[#C77D63] text-[#C77D63] hover:bg-[#C77D63]/5 rounded-full px-6 py-2 mb-4"
                data-testid="redownload-pdf-btn"
              >
                {isDownloading ? "Downloading..." : "Download Again"}
              </Button>
              
              {/* Scope Clarifier */}
              <p className="text-[10px] text-[#A8A29E] bg-[#FDFCF8] rounded-lg p-3 mb-4">
                This guide provides direction and clarity.<br/>
                It does not include templates, formulas, domains, or ready-made brand assets.
              </p>
              
              {/* Close / Done */}
              <button
                onClick={() => setShowDownloadSuccess(false)}
                className="text-sm text-[#78716C] hover:text-[#2C332E]"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-6 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <p className="text-[#C77D63] font-accent text-xl mb-2">Your Brand is Ready</p>
          <h1 className="text-4xl md:text-5xl tracking-tight text-[#2C332E] mb-4">
            Meet Your New Brand
          </h1>
          <p className="text-[#78716C] max-w-2xl mx-auto">
            {suggestions.length === 1 
              ? "We've created a unique brand concept based on your answers. Review and save it to get started."
              : `We've created ${suggestions.length} unique brand concepts based on your answers. Explore each one and save your favorite.`
            }
          </p>
        </div>

        {/* Brand Selector */}
        {suggestions.length > 1 && (
          <div className="flex items-center justify-center gap-4 mb-8">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setSelectedBrandIndex(Math.max(0, selectedBrandIndex - 1))}
              disabled={selectedBrandIndex === 0}
              className="rounded-full"
              data-testid="prev-brand-btn"
            >
              <ChevronLeft className="w-6 h-6" />
            </Button>
            <div className="flex gap-2">
              {suggestions.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setSelectedBrandIndex(index)}
                  className={`w-3 h-3 rounded-full transition-colors ${
                    index === selectedBrandIndex ? 'bg-[#C77D63]' : 'bg-[#E6E1DB]'
                  }`}
                  data-testid={`brand-selector-${index}`}
                />
              ))}
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setSelectedBrandIndex(Math.min(suggestions.length - 1, selectedBrandIndex + 1))}
              disabled={selectedBrandIndex === suggestions.length - 1}
              className="rounded-full"
              data-testid="next-brand-btn"
            >
              <ChevronRight className="w-6 h-6" />
            </Button>
          </div>
        )}

        {/* Brand Name & Tagline */}
        <Card className="bg-white rounded-2xl border-[#E6E1DB]/50 shadow-soft mb-8 overflow-hidden">
          {/* Color Palette Preview */}
          <div className="h-4 flex">
            {getColorPaletteArray(selectedBrand.color_palette).map((color, index) => (
              <div 
                key={index}
                className="flex-1"
                style={{ backgroundColor: color.hex }}
              />
            ))}
          </div>
          <CardContent className="p-8 text-center">
            <h2 className="text-4xl md:text-5xl tracking-tight text-[#2C332E] mb-4" data-testid="brand-name">
              {selectedBrand.brand_name}
            </h2>
            <p className="text-xl text-[#78716C] italic" data-testid="brand-tagline">
              "{selectedBrand.tagline}"
            </p>
          </CardContent>
        </Card>

        {/* Tabs */}
        <Tabs defaultValue="palette" className="space-y-6">
          <TabsList className="bg-[#F5F0EB] p-1 rounded-full w-full justify-start overflow-x-auto">
            <TabsTrigger 
              value="palette" 
              className="rounded-full data-[state=active]:bg-white data-[state=active]:text-[#2C332E]"
            >
              <Palette className="w-4 h-4 mr-2" />
              Color Palette
            </TabsTrigger>
            <TabsTrigger 
              value="tone" 
              className="rounded-full data-[state=active]:bg-white data-[state=active]:text-[#2C332E]"
            >
              <MessageSquare className="w-4 h-4 mr-2" />
              Tone & Values
            </TabsTrigger>
            <TabsTrigger 
              value="story" 
              className="rounded-full data-[state=active]:bg-white data-[state=active]:text-[#2C332E]"
            >
              <Sparkles className="w-4 h-4 mr-2" />
              Brand Story
            </TabsTrigger>
            <TabsTrigger 
              value="guide" 
              className="rounded-full data-[state=active]:bg-white data-[state=active]:text-[#2C332E]"
            >
              <BookOpen className="w-4 h-4 mr-2" />
              Business Guide
            </TabsTrigger>
            <TabsTrigger 
              value="packaging" 
              className="rounded-full data-[state=active]:bg-white data-[state=active]:text-[#2C332E]"
            >
              <Package className="w-4 h-4 mr-2" />
              Packaging
            </TabsTrigger>
          </TabsList>

          {/* Color Palette Tab */}
          <TabsContent value="palette">
            <Card className="bg-white rounded-2xl border-[#E6E1DB]/50 shadow-soft">
              <CardHeader>
                <CardTitle className="text-2xl text-[#2C332E]">Your Color Palette</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-5 gap-4">
                  {getColorPaletteArray(selectedBrand.color_palette).map((color, index) => (
                    <div key={index} className="text-center" data-testid={`color-${index}`}>
                      <div 
                        className="w-full aspect-square rounded-2xl mb-3 shadow-soft"
                        style={{ backgroundColor: color.hex }}
                      />
                      <p className="font-semibold text-[#2C332E]">{color.name}</p>
                      <p className="text-sm text-[#78716C] uppercase">{color.hex}</p>
                      <p className="text-xs text-[#8DA399] mt-1">{color.usage}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Tone & Values Tab */}
          <TabsContent value="tone">
            <div className="grid md:grid-cols-2 gap-6">
              <Card className="bg-white rounded-2xl border-[#E6E1DB]/50 shadow-soft">
                <CardHeader>
                  <CardTitle className="text-2xl text-[#2C332E]">Tone of Voice</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-[#78716C] leading-relaxed" data-testid="tone-of-voice">
                    {selectedBrand.tone_of_voice}
                  </p>
                </CardContent>
              </Card>

              <Card className="bg-white rounded-2xl border-[#E6E1DB]/50 shadow-soft">
                <CardHeader>
                  <CardTitle className="text-2xl text-[#2C332E]">Brand Values</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    {getBrandValuesArray(selectedBrand.brand_values).map((value, index) => (
                      <span 
                        key={index}
                        className="px-4 py-2 bg-[#8DA399]/10 text-[#8DA399] rounded-full flex items-center gap-2"
                        data-testid={`brand-value-${index}`}
                      >
                        <Check className="w-4 h-4" />
                        {value}
                      </span>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Brand Story Tab */}
          <TabsContent value="story">
            <Card className="bg-white rounded-2xl border-[#E6E1DB]/50 shadow-soft">
              <CardHeader>
                <CardTitle className="text-2xl text-[#2C332E]">Your Brand Story</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="prose max-w-none">
                  <p className="text-[#78716C] leading-relaxed whitespace-pre-line" data-testid="brand-story">
                    {selectedBrand.brand_story}
                  </p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Business Guide Tab */}
          <TabsContent value="guide">
            <Card className="bg-white rounded-2xl border-[#E6E1DB]/50 shadow-soft">
              <CardHeader>
                <CardTitle className="text-2xl text-[#2C332E]">Your Business Launch Guide</CardTitle>
              </CardHeader>
              <CardContent>
                {(() => {
                  const bg = selectedBrand?.business_guide;
                  
                  // New format: object with phase1_offer, next_step, etc.
                  if (bg && typeof bg === 'object' && !Array.isArray(bg)) {
                    return (
                      <div className="space-y-4">
                        <p className="text-[#78716C] mb-6">A calm framework for moving forward — without pressure.</p>
                        {bg.phase1_offer && (
                          <div className="flex gap-4 p-4 rounded-xl bg-[#F5F0EB]/50">
                            <div className="step-circle completed flex-shrink-0">1</div>
                            <div>
                              <h4 className="font-semibold text-[#2C332E] mb-1">Start With</h4>
                              <p className="text-[#78716C] leading-relaxed">{bg.phase1_offer}</p>
                            </div>
                          </div>
                        )}
                        {bg.next_step && (
                          <div className="flex gap-4 p-4 rounded-xl bg-[#F5F0EB]/50">
                            <div className="step-circle completed flex-shrink-0">2</div>
                            <div>
                              <h4 className="font-semibold text-[#2C332E] mb-1">One Next Step This Week</h4>
                              <p className="text-[#78716C] leading-relaxed">{bg.next_step}</p>
                            </div>
                          </div>
                        )}
                        {bg.starter_channel && (
                          <div className="flex gap-4 p-4 rounded-xl bg-[#F5F0EB]/50">
                            <div className="step-circle completed flex-shrink-0">3</div>
                            <div>
                              <h4 className="font-semibold text-[#2C332E] mb-1">One Channel to Begin With</h4>
                              <p className="text-[#78716C] leading-relaxed">{bg.starter_channel}</p>
                            </div>
                          </div>
                        )}
                        {bg.milestone && (
                          <div className="flex gap-4 p-4 rounded-xl bg-[#F5F0EB]/50">
                            <div className="step-circle completed flex-shrink-0">4</div>
                            <div>
                              <h4 className="font-semibold text-[#2C332E] mb-1">A Calm First Milestone</h4>
                              <p className="text-[#78716C] leading-relaxed">{bg.milestone}</p>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  }
                  
                  // Old format: array of steps
                  if (Array.isArray(bg)) {
                    return (
                      <div className="space-y-6">
                        {bg.map((step, index) => (
                          <div 
                            key={index}
                            className="flex gap-4 p-4 rounded-xl hover:bg-[#F5F0EB] transition-colors"
                            data-testid={`guide-step-${index}`}
                          >
                            <div className="step-circle completed flex-shrink-0">
                              {step.step}
                            </div>
                            <div>
                              <h4 className="font-semibold text-[#2C332E] mb-1">{step.title}</h4>
                              <p className="text-[#78716C] leading-relaxed">{step.description}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    );
                  }
                  
                  // Fallback
                  return <p className="text-[#78716C]">Business guide coming soon...</p>;
                })()}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Packaging Vendors Tab */}
          <TabsContent value="packaging">
            <Card className="bg-white rounded-2xl border-[#E6E1DB]/50 shadow-soft">
              <CardHeader>
                <CardTitle className="text-2xl text-[#2C332E]">Recommended Packaging Vendors</CardTitle>
                <p className="text-[#78716C] mt-2">Trusted suppliers for your {questionnaire?.business_type?.replace(/_/g, ' ')} products</p>
              </CardHeader>
              <CardContent>
                {packagingVendors.length > 0 ? (
                  <div className="grid md:grid-cols-3 gap-4">
                    {packagingVendors.map((vendor, index) => (
                      <div 
                        key={index}
                        className="p-4 rounded-xl border border-[#E6E1DB] hover:border-[#8DA399] hover:shadow-soft transition-all"
                        data-testid={`vendor-${index}`}
                      >
                        <div className="flex items-start justify-between mb-3">
                          <h4 className="font-semibold text-[#2C332E]">{vendor.name}</h4>
                          <a 
                            href={vendor.url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-[#8DA399] hover:text-[#C77D63] transition-colors"
                          >
                            <ExternalLink className="w-4 h-4" />
                          </a>
                        </div>
                        <p className="text-sm text-[#78716C]">{vendor.specialty}</p>
                        <a 
                          href={vendor.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-sm text-[#8DA399] hover:text-[#C77D63] mt-3 transition-colors"
                        >
                          Visit Website
                          <ExternalLink className="w-3 h-3" />
                        </a>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-[#78716C] text-center py-8">No packaging vendors available for this category</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Save CTA */}
        <div className="text-center mt-12">
          <Button 
            onClick={handleSaveBrand}
            disabled={isSaving || savedBrandId}
            className={`rounded-full px-8 py-6 text-lg hover-lift ${savedBrandId ? 'bg-[#8DA399] hover:bg-[#8DA399]' : 'bg-[#C77D63] hover:bg-[#B06A52]'} text-white`}
            data-testid="save-brand-bottom-btn"
          >
            {isSaving ? "Saving..." : savedBrandId ? "✓ Brand Saved" : "Save This Brand to Your Profile"}
          </Button>
        </div>

        {/* Gentle Guidance Section */}
        <div className="mt-16 pt-8 border-t border-[#E6E1DB]">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-8">
              <p className="text-[#78716C] text-sm leading-relaxed">
                When you're ready, here are a few gentle ways to begin using what you've created.<br/>
                <span className="text-[#A8A29E]">This is guidance only — nothing here needs to be opened, shared, or finalized yet.</span>
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-6">
              {/* Card 1 - Use it visually */}
              <div className="bg-white rounded-xl p-5 border border-[#E6E1DB]">
                <div className="flex items-center gap-2 mb-3">
                  <Palette className="w-5 h-5 text-[#C77D63]" />
                  <h4 className="font-medium text-[#2C332E]">Use it visually</h4>
                </div>
                <p className="text-sm text-[#78716C] mb-3">
                  Your colors, tone, and overall feel are already defined. You can use them as inspiration for simple visuals — a post idea, a label concept, or a mood reference — without building a full design system yet.
                </p>
                <p className="text-xs text-[#A8A29E] italic">
                  Simple is enough. One visual helps your brand feel real.
                </p>
              </div>

              {/* Card 2 - Use it online */}
              <div className="bg-white rounded-xl p-5 border border-[#E6E1DB]">
                <div className="flex items-center gap-2 mb-3">
                  <ExternalLink className="w-5 h-5 text-[#C77D63]" />
                  <h4 className="font-medium text-[#2C332E]">Use it online</h4>
                </div>
                <p className="text-sm text-[#78716C] mb-3">
                  Your brand story and values can guide how you describe what you do — on a website, a shop page, or even a short bio. You don't need to publish anything yet. This is about clarity, not visibility.
                </p>
                <p className="text-xs text-[#A8A29E] italic">
                  Use your words as a draft, not a final version.
                </p>
              </div>

              {/* Card 3 - Use it on packaging */}
              <div className="bg-white rounded-xl p-5 border border-[#E6E1DB]">
                <div className="flex items-center gap-2 mb-3">
                  <Package className="w-5 h-5 text-[#C77D63]" />
                  <h4 className="font-medium text-[#2C332E]">Use it on packaging</h4>
                </div>
                <p className="text-sm text-[#78716C] mb-3">
                  This brand was designed to translate into the physical world. When you're ready, your colors, tone, and values can guide packaging choices that feel aligned — even for small batches or simple labels.
                </p>
                <p className="text-xs text-[#A8A29E] italic">
                  You're not choosing packaging today — just understanding what fits.
                </p>
              </div>
            </div>

            {/* Reinforcement line */}
            <p className="text-center text-xs text-[#A8A29E] mt-6">
              Your full Brand Guide is available as a downloadable PDF after checkout.
            </p>
          </div>
        </div>

        {/* Download Section */}
        <div className="mt-16 pt-8 border-t border-[#E6E1DB]">
          <div className="text-center max-w-lg mx-auto">
            <h3 className="text-lg font-semibold text-[#2C332E] mb-2">
              Your Brand Guide
            </h3>
            <p className="text-sm text-[#78716C] mb-2">
              Your complete brand overview — ready to save, review, and return to when you're ready.
            </p>
            
            {!savedBrandId ? (
              <div className="text-center">
                <Button 
                  onClick={() => toast.info("Save your brand first to unlock your Brand Guide")}
                  variant="outline"
                  className="border-[#E6E1DB] text-[#78716C] rounded-full px-6 py-3 mt-4"
                  data-testid="download-brand-bottom-btn-disabled"
                >
                  <Lock className="w-4 h-4 mr-2" />
                  Save brand to unlock
                </Button>
                <p className="text-xs text-[#A8A29E] mt-3">
                  Your brand details are saved. You'll be able to download immediately after checkout.
                </p>
              </div>
            ) : !hasPurchased ? (
              <div className="text-center">
                <Button 
                  onClick={handleUnlockClick}
                  className="bg-[#C77D63] hover:bg-[#B06A52] text-white rounded-full px-6 py-3 mt-4"
                  data-testid="unlock-brand-bottom-btn"
                >
                  <Lock className="w-4 h-4 mr-2" />
                  Unlock Brand Guide — $47
                </Button>
                <p className="text-xs text-[#A8A29E] mt-3">
                  Your brand details are saved. You'll be able to download immediately after checkout.
                </p>
              </div>
            ) : (
              <div className="text-center">
                <Button 
                  onClick={handleDownload}
                  disabled={isDownloading}
                  className="bg-[#C77D63] hover:bg-[#B06A52] text-white rounded-full px-6 py-3 mt-4"
                  data-testid="download-brand-bottom-btn"
                >
                  {isDownloading ? (
                    <>
                      <div className="spinner w-4 h-4 mr-2 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Downloading...
                    </>
                  ) : (
                    <>
                      <Download className="w-4 h-4 mr-2" />
                      Generate & Download PDF
                    </>
                  )}
                </Button>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
    </PayPalScriptProvider>
  );
};

export default BrandResults;
